import { toast } from 'react-toastify/unstyled';

import type { ArtifactContent, CreateAgentResponse } from '@features/integrations/api/create-agent.types';
import type { RTKQueryError } from '@shared/utils/api.types';

import type { AgentDetailsFormValues } from './sub-components/general/generic-agent-details.schema';

export const AGENT_TYPE = {
  STANDALONE_AGENT: 'STANDALONE',
  OP_AGENT: 'OP',
} as const;

export const STEP = {
  AGENT_TYPE_SELECTION: 'AGENT_TYPE_SELECTION',
  STANDALONE_AGENT_UPLOAD_ARTIFACT: 'STANDALONE_AGENT_UPLOAD_ARTIFACT',
  STANDALONE_AGENT_ENTER_DETAILS: 'STANDALONE_AGENT_ENTER_DETAILS',
  STANDALONE_AGENT_GENERATE_BUNDLE: 'STANDALONE_AGENT_GENERATE_BUNDLE',
  OP_AGENT_ENTER_DETAILS: 'OP_AGENT_ENTER_DETAILS',
  OP_AGENT_GENERATE_BUNDLE: 'OP_AGENT_GENERATE_BUNDLE',
} as const;

export const SAP_ARCHITECTURES = ['LinuxAmd64', 'LinuxArm64', 'WindowsAmd64'] as const;
export type AgentType = typeof AGENT_TYPE.STANDALONE_AGENT | typeof AGENT_TYPE.OP_AGENT | null;

export type Step = (typeof STEP)[keyof typeof STEP];

export const STEP_LABELS: Record<Step, string> = {
  [STEP.AGENT_TYPE_SELECTION]: '',

  // Standalone Agent Steps
  [STEP.STANDALONE_AGENT_UPLOAD_ARTIFACT]: 'Step 1 of 3',
  [STEP.STANDALONE_AGENT_ENTER_DETAILS]: 'Step 2 of 3',
  [STEP.STANDALONE_AGENT_GENERATE_BUNDLE]: 'Step 3 of 3',

  // OP Integration Steps
  [STEP.OP_AGENT_ENTER_DETAILS]: 'Step 1 of 2',
  [STEP.OP_AGENT_GENERATE_BUNDLE]: 'Step 2 of 2',
};

export interface BaseAgentDetailsProps {
  onGenerateSuccess: (response: CreateAgentResponse, payload: AgentDetailsFormValues, nextStep: Step) => void;
  onClose: () => void;
}

interface StepContentConfig {
  headerTitle: string;
  stepTitle: string;
  stepDetails: string;
}

export const STEP_CONTENT_MAP: Partial<Record<Step, StepContentConfig>> = {
  [STEP.STANDALONE_AGENT_UPLOAD_ARTIFACT]: {
    headerTitle: 'Generate Standalone Agent',
    stepTitle: 'File Upload',
    stepDetails: 'Upload the required SAP artifact (ex. sapjco3.zip)',
  },
  [STEP.STANDALONE_AGENT_ENTER_DETAILS]: {
    headerTitle: 'Generate Standalone Agent',
    stepTitle: 'Agent Details',
    stepDetails: 'Please provide the following details for the standalone agent.',
  },
  [STEP.STANDALONE_AGENT_GENERATE_BUNDLE]: {
    headerTitle: 'Generate Standalone Agent',
    stepTitle: 'Standalone agent deployment package link',
    stepDetails:
      'To complete deployment, download the package below and transfer it to your target server host. Then unpack it to establish the SAP connection.',
  },
  [STEP.OP_AGENT_ENTER_DETAILS]: {
    headerTitle: 'Onapsis Platform (OP) Integration',
    stepTitle: 'Agent Details',
    stepDetails: 'Please provide the following details for the OP agent.',
  },
  [STEP.OP_AGENT_GENERATE_BUNDLE]: {
    headerTitle: 'Onapsis Platform (OP) Integration',
    stepTitle: 'OP deployment package link',
    stepDetails:
      'To complete deployment, download the package below and transfer it to your target server host. Then unpack it to establish the SAP connection.',
  },
};

// Checks if the artifacts list contains the required 'jco' component.
export const getHasRequiredArtifacts = (artifacts?: ArtifactContent[]): boolean => {
  if (!artifacts || artifacts.length === 0) {
    return false;
  }

  return artifacts.some((item) => item.component === 'jco');
};

export const handleAgentSubmissionError = (error: unknown, setErrorField: (message: string) => void) => {
  const rtkError = error as {
    status?: number;
    data?: RTKQueryError;
  };

  console.error('Error submitting form:', error);

  const isDuplicateNameError = rtkError?.status === 409 && rtkError?.data?.message?.includes('name already exists');

  if (isDuplicateNameError) {
    setErrorField('Agent name already exists. Please provide a different name.');
  } else {
    toast.error('An unexpected error occurred while generating the package link.');
  }
};

export const cleanAgentDescription = (description?: string) => {
  return description && description.trim() !== '' ? { description: description.trim() } : {};
};
